import React, { useRef } from "react";
import { CircularProgress, Tooltip } from "@mui/material";
import { StyledButton } from "../../styled/TitleBar";
import { Instance } from "@popperjs/core";

interface LoadingButtonProps {
  variant?: "contained" | "outlined" | "text"; // Button variant
  onClick?: () => void; // Action to perform on click
  disabled?: boolean; // Disable the button
  isLoading?: boolean; // Show loading spinner
  icon?: React.ReactNode; // Icon to display
  text?: string; // Button text
  tooltipText?: string; // Tooltip text (optional)
  style?: React.CSSProperties; // Optional custom styles
  isCancelling?: boolean; // Flag to indicate if the button is in a cancelling state
}

const LoadingButton: React.FC<LoadingButtonProps> = ({
  variant = "contained",
  onClick,
  disabled = false,
  isLoading = false,
  icon,
  text,
  tooltipText,
  style,
  isCancelling = false, // New prop for cancelling state
}) => {
  const buttonContent = (
    <>
      {icon}
      {isLoading && !isCancelling && <CircularProgress size={20} style={{ marginLeft: 8 }} />}
      <span style={{ marginLeft: isLoading ? 8 : 0 }}>{text}</span>
    </>
  );

  const positionRef = useRef<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  const popperRef = useRef<Instance>(null);
  const areaRef = useRef<HTMLDivElement>(null);

  return (
    <Tooltip
      title={tooltipText}
      placement="top"
      arrow
      disableFocusListener
      disableHoverListener={!disabled}
      disableTouchListener
      leaveTouchDelay={500}
      slotProps={{
        popper: {
          popperRef,
          anchorEl: {
            getBoundingClientRect: () => {
              return new DOMRect(
                positionRef.current.x,
                areaRef.current!.getBoundingClientRect().y,
                0,
                0
              );
            },
          },
        },
      }}
    >
      <div ref={areaRef}>
        <StyledButton
          variant={variant}
          onClick={onClick}
          disabled={disabled || isLoading || isCancelling}
          style={style} // Apply custom styles
        >
          {buttonContent}
          </StyledButton>
      </div>
    </Tooltip>
  );
};

export default LoadingButton;